<template>
    <Link
        :href="index({ path: item.url_key })"
        class="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition-[translate,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg"
    >
        <div class="aspect-square w-full overflow-hidden px-3 pt-3 md:px-4 md:pt-4">
            <img
                v-if="item.firstImage"
                :src="resolvedImageUrl(item.firstImage, ImageTypeEnum.Mobile)"
                :alt="item.name"
                class="h-full w-full object-contain"
                loading="lazy"
            />
        </div>

        <div class="flex flex-1 flex-col gap-3 p-3 md:p-4">
            <!-- Title and stars as one tight block; the title is always two lines tall, so the stars line up on every card. -->
            <div class="flex flex-col gap-1">
                <p class="line-clamp-2 min-h-[2lh] text-left text-sm leading-tight font-bold md:text-lg" v-text="shortName" />

                <div class="text-xs sm:text-base">
                    <ReviewStars
                        v-if="item.votes"
                        size="sm"
                        size-mobile="xs"
                        :review-score="item.rating ?? undefined"
                        :review-count="item.votes ?? 0"
                        show-score
                        show-rating
                    />
                </div>
            </div>

            <div class="mt-auto">
                <div class="flex items-center gap-2 md:gap-3">
                    <span
                        v-if="item.price"
                        class="inline-flex items-baseline gap-1 rounded-lg bg-prices px-2 py-1 text-sm leading-none font-black text-white md:px-2.5 md:py-1.5 md:text-lg"
                    >
                        <span class="text-xs md:text-sm">{{ $t('pages.product_detail_page.price.from_abbreviation') }}</span>
                        <span>{{ formatPrice(item.price) }}</span>
                    </span>

                    <span v-if="item.discount" class="text-sm leading-none font-bold whitespace-nowrap text-prices md:text-lg"
                        >-&nbsp;{{ item.discount }}%</span
                    >
                </div>
            </div>

            <p :class="{ invisible: !hasRecommendedPrice }" class="-mt-1 text-xs leading-none text-recommended-prices md:text-sm">
                {{ $t('pages.product_detail_page.price.instead_of') }}
                <span v-if="hasRecommendedPrice" class="line-through">{{ formatPrice(item.recommended_price!) }}</span>
            </p>
        </div>
    </Link>
</template>

<script setup lang="ts">
import ReviewStars from '@/components/reviews/ReviewStars.vue';
import { useProductName } from '@/composables/products/ProductNameComposable';
import { useFormatPrice } from '@/composables/useFormatPrice';
import type { SamePrintProductObject } from '@/types/generated';
import { index } from '@/types/wayfinder/routes';
import { ImageTypeEnum, resolvedImageUrl } from '@/utils/imageUrl';
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';

const props = defineProps<{
    item: SamePrintProductObject;
}>();

const formatPrice = useFormatPrice();

const { collectionName: shortName } = useProductName(() => props.item.name);

// Only a recommended price above the selling price is worth striking through.
const hasRecommendedPrice = computed(
    (): boolean => props.item.recommended_price !== null && props.item.price !== null && props.item.recommended_price > props.item.price,
);
</script>
